import React from "react";
import { OAuthServiceType } from "@common/constants";
import { FinancialConnection } from "@common/types/financialConnection";
import {
    ConnectionProvider,
    IntegrationConnection,
} from "@common/types/integrationConnection";
import { IntegrationAccount } from "@common/types/integrationAccount";
import { PlaidConnection } from "@common/types/plaidConnection";
import {
    isFixablePlaidConnectionError,
    isPlaidConnection,
} from "@common/helpers/financialConnection";
import { isIntegrationConnection } from "@common/helpers/integrationConnection";
import {
    PLAID_PAYPAL_INSTITUTION_ID,
    PlaidAccountMetadata,
} from "@common/helpers/plaid";
import { CreatePayPalConnection } from "@components/paypal/CreatePayPalConnection";
import { usePayPalConnectionFix } from "@components/paypal/usePayPalConnectionFix";
import { usePlaidConnectionFix } from "@components/plaid/usePlaidConnectionFix";
import {
    ConnectionFix,
    useIntegrationOAuthConnectionFix,
} from "./useOAuthConnectionFix";
import {
    FINANCIAL_OAUTH_FIX_CONFIGS,
    FinancialOAuthFixConfig,
    useFinancialConnectionOAuthFix,
} from "./useFinancialConnectionOAuthFix";

export type ConnectionFixerState =
    | { canFix: false }
    | ({ canFix: true } & ConnectionFix);

type RenderFixer = (state: ConnectionFixerState) => React.ReactNode;

interface FixerProps<TConnection> {
    connection: TConnection;
    onFixed?: () => void;
    children: RenderFixer;
}

const UNFIXABLE: ConnectionFixerState = { canFix: false };

const INTEGRATION_OAUTH_SCOPES: Partial<
    Record<ConnectionProvider, OAuthServiceType>
> = {
    [ConnectionProvider.MERCURY]: OAuthServiceType.MERCURY,
    [ConnectionProvider.QUICKBOOKS]: OAuthServiceType.QUICKBOOKS,
    [ConnectionProvider.GUSTO]: OAuthServiceType.GUSTO,
    [ConnectionProvider.STRIPE]: OAuthServiceType.STRIPE,
};

const IntegrationOAuthFixer = ({
    scope,
    children,
    ...options
}: FixerProps<IntegrationConnection> & { scope: OAuthServiceType }) => (
    <>
        {children({
            ...useIntegrationOAuthConnectionFix({ ...options, scope }),
            canFix: true,
        })}
    </>
);

const PayPalFixer = ({
    children,
    ...options
}: FixerProps<IntegrationConnection>) => (
    <>{children({ ...usePayPalConnectionFix(options), canFix: true })}</>
);

const FinancialOAuthFixer = ({
    children,
    ...options
}: FixerProps<FinancialConnection> & { config: FinancialOAuthFixConfig }) => (
    <>
        {children({
            ...useFinancialConnectionOAuthFix(options),
            canFix: true,
        })}
    </>
);

const PlaidFixer = ({
    children,
    ...options
}: FixerProps<PlaidConnection> & { isAccountChurned: boolean }) => (
    <>
        {children({
            ...usePlaidConnectionFix(options),
            canFix: true,
        })}
    </>
);

const PlaidPayPalFixer = ({
    connection,
    onFixed,
    children,
}: FixerProps<PlaidConnection>) => (
    <CreatePayPalConnection
        plaidConnectionToMigrate={connection}
        onCreated={onFixed}
    >
        {({ connecting, initializeConnection }) =>
            children({
                startFix: initializeConnection,
                isFixing: connecting,
                canFix: true,
            })
        }
    </CreatePayPalConnection>
);

interface ConnectionFixerProps extends FixerProps<
    FinancialConnection | IntegrationConnection
> {
    integrationAccount?: IntegrationAccount;
}

export const ConnectionFixer = ({
    connection,
    integrationAccount,
    onFixed,
    children,
}: ConnectionFixerProps) => {
    if (isIntegrationConnection(connection)) {
        if (connection.provider === ConnectionProvider.PAYPAL) {
            return (
                <PayPalFixer connection={connection} onFixed={onFixed}>
                    {children}
                </PayPalFixer>
            );
        }
        const scope = INTEGRATION_OAUTH_SCOPES[connection.provider];
        if (!scope) {
            return <>{children(UNFIXABLE)}</>;
        }
        return (
            <IntegrationOAuthFixer
                connection={connection}
                scope={scope}
                onFixed={onFixed}
            >
                {children}
            </IntegrationOAuthFixer>
        );
    }

    if (isPlaidConnection(connection)) {
        if (!isFixablePlaidConnectionError(connection.error)) {
            return <>{children(UNFIXABLE)}</>;
        }
        if (connection.institutionId === PLAID_PAYPAL_INSTITUTION_ID) {
            return (
                <PlaidPayPalFixer connection={connection} onFixed={onFixed}>
                    {children}
                </PlaidPayPalFixer>
            );
        }
        return (
            <PlaidFixer
                connection={connection}
                isAccountChurned={Boolean(
                    integrationAccount?.metadata?.[
                        PlaidAccountMetadata.ACCOUNT_CHURN
                    ],
                )}
                onFixed={onFixed}
            >
                {children}
            </PlaidFixer>
        );
    }

    const config = FINANCIAL_OAUTH_FIX_CONFIGS[connection.connectionProvider];
    if (!config) {
        return <>{children(UNFIXABLE)}</>;
    }
    return (
        <FinancialOAuthFixer
            key={connection.connectionProvider}
            connection={connection}
            config={config}
            onFixed={onFixed}
        >
            {children}
        </FinancialOAuthFixer>
    );
};
